import { useCallback, useRef, useState, type RefObject } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { notifyOfflineActionBlocked } from "../utils/internetConnection";

const DROP_TARGET_ATTR = "data-interactive-drop";
const DRAG_EDGE_PADDING = 12;
const DRAG_THRESHOLD_PX = 6;
const CONTAINER_SCROLL_EDGE = 56;
const AUTO_SCROLL_MAX_SPEED = 14;

const VIEWPORT_SCROLL_EDGE = 72;

type TouchGhostState = {
  text: string;
  relativeX: number;
  relativeY: number;
};

type UseOptionTransferArgs = {
  disabled: boolean;
  isBrowserLocked: boolean;
  isOffline?: boolean;
  containerRef?: RefObject<HTMLElement | null>;
};

function clampToContainer(
  clientX: number,
  clientY: number,
  rect: DOMRect,
): { x: number; y: number } {
  return {
    x: Math.min(
      Math.max(clientX, rect.left + DRAG_EDGE_PADDING),
      rect.right - DRAG_EDGE_PADDING,
    ),
    y: Math.min(
      Math.max(clientY, rect.top + DRAG_EDGE_PADDING),
      rect.bottom - DRAG_EDGE_PADDING,
    ),
  };
}

function isPointInRect(
  clientX: number,
  clientY: number,
  rect: DOMRect,
): boolean {
  return (
    clientX >= rect.left &&
    clientX <= rect.right &&
    clientY >= rect.top &&
    clientY <= rect.bottom
  );
}

function findScrollableWithinBoundary(
  from: HTMLElement,
  boundary: HTMLElement,
): HTMLElement | null {
  let el: HTMLElement | null = from;
  while (el && boundary.contains(el)) {
    const { overflowY } = getComputedStyle(el);
    if (
      (overflowY === "auto" ||
        overflowY === "scroll" ||
        overflowY === "overlay") &&
      el.scrollHeight > el.clientHeight + 1
    ) {
      return el;
    }
    if (el === boundary) break;
    el = el.parentElement;
  }
  return null;
}

function resolveScrollRoot(from: HTMLElement | null): HTMLElement | null {
  if (!from) return null;

  const assessmentScroll = from.closest(
    "[data-assessment-scroll]",
  ) as HTMLElement | null;
  if (assessmentScroll) {
    if (assessmentScroll.scrollHeight > assessmentScroll.clientHeight + 1) {
      return assessmentScroll;
    }
  }

  const tabbedScroll = from.closest(
    "[data-tabbed-scroll]",
  ) as HTMLElement | null;
  if (tabbedScroll && !assessmentScroll) {
    const innerScrollable = findScrollableWithinBoundary(from, tabbedScroll);
    if (innerScrollable) return innerScrollable;
    if (tabbedScroll.scrollHeight > tabbedScroll.clientHeight + 1) {
      return tabbedScroll;
    }
    const chatScroll = tabbedScroll.closest(
      ".messages-container-chat, .messages-container",
    ) as HTMLElement | null;
    if (chatScroll && chatScroll.scrollHeight > chatScroll.clientHeight + 1) {
      return chatScroll;
    }
    return null;
  }

  const known = from.closest(
    ".messages-container-chat, .messages-container",
  ) as HTMLElement | null;
  if (known && known.scrollHeight > known.clientHeight + 1) {
    return known;
  }

  let el: HTMLElement | null = from;
  while (el) {
    const { overflowY } = getComputedStyle(el);
    if (
      (overflowY === "auto" ||
        overflowY === "scroll" ||
        overflowY === "overlay") &&
      el.scrollHeight > el.clientHeight + 1
    ) {
      return el;
    }
    el = el.parentElement;
  }

  return null;
}

const DRAG_DIRECTION_THRESHOLD = 2;
const SCROLL_POSITION_EPSILON = 1;
const EDGE_OVERSHOOT = VIEWPORT_SCROLL_EDGE;

function getTabbedMinScrollTop(
  scrollRoot: HTMLElement,
  messageBoundary: HTMLElement,
): number {
  const scrollRect = scrollRoot.getBoundingClientRect();
  const messageRect = messageBoundary.getBoundingClientRect();
  return Math.max(
    0,
    scrollRoot.scrollTop + messageRect.top - scrollRect.top,
  );
}

function getTabbedScrollRoomUp(
  scrollRoot: HTMLElement,
  messageBoundary: HTMLElement | null,
): number {
  if (!messageBoundary) return scrollRoot.scrollTop;
  const minScrollTop = getTabbedMinScrollTop(scrollRoot, messageBoundary);
  return Math.max(0, scrollRoot.scrollTop - minScrollTop);
}

function getAutoScrollEdgeRect(
  scrollRoot: HTMLElement,
  tabbedBoundary: HTMLElement | null,
): DOMRect {
  const scrollRect = scrollRoot.getBoundingClientRect();
  if (!tabbedBoundary) return scrollRect;

  const tabRect = tabbedBoundary.getBoundingClientRect();
  const top = Math.max(tabRect.top, scrollRect.top);
  const bottom = Math.min(tabRect.bottom, scrollRect.bottom);
  const left = Math.max(tabRect.left, scrollRect.left);
  const right = Math.min(tabRect.right, scrollRect.right);

  return new DOMRect(
    left,
    top,
    Math.max(0, right - left),
    Math.max(0, bottom - top),
  );
}

function shouldAutoScroll(
  rawX: number,
  rawY: number,
  containerRect: DOMRect,
  scrollRect: DOMRect,
  tabbedBoundary: HTMLElement | null,
): boolean {
  if (isPointInRect(rawX, rawY, containerRect)) return true;
  if (tabbedBoundary) {
    if (
      isPointInRect(
        rawX,
        rawY,
        tabbedBoundary.getBoundingClientRect(),
      )
    ) {
      return true;
    }
    return isPointInRect(rawX, rawY, scrollRect);
  }
  return isPointInRect(rawX, rawY, scrollRect);
}

function getViewportEdgeScrollDelta(
  rawY: number,
  dragDeltaY: number,
  scrollRoot: HTMLElement,
  tabbedBoundary: HTMLElement | null,
  containerRect: DOMRect | null,
): number {
  const edgeRect = getAutoScrollEdgeRect(scrollRoot, tabbedBoundary);
  const maxScrollTop = Math.max(
    0,
    scrollRoot.scrollHeight - scrollRoot.clientHeight,
  );
  const scrollRoomDown = maxScrollTop - scrollRoot.scrollTop;
  const scrollRoomUp = getTabbedScrollRoomUp(scrollRoot, tabbedBoundary);

  const distFromTop = rawY - edgeRect.top;
  const distFromBottom = edgeRect.bottom - rawY;
  const nearContainerTop =
    containerRect !== null &&
    rawY - containerRect.top < CONTAINER_SCROLL_EDGE;
  const nearContainerBottom =
    containerRect !== null &&
    containerRect.bottom - rawY < CONTAINER_SCROLL_EDGE;

  if (
    distFromTop > -EDGE_OVERSHOOT &&
    distFromTop < VIEWPORT_SCROLL_EDGE &&
    scrollRoomUp > SCROLL_POSITION_EPSILON
  ) {
    const intensity =
      distFromTop <= 0 ? 1 : 1 - distFromTop / VIEWPORT_SCROLL_EDGE;
    return -AUTO_SCROLL_MAX_SPEED * intensity;
  }

  if (
    distFromBottom > -EDGE_OVERSHOOT &&
    distFromBottom < VIEWPORT_SCROLL_EDGE &&
    scrollRoomDown > SCROLL_POSITION_EPSILON
  ) {
    const intensity =
      distFromBottom <= 0 ? 1 : 1 - distFromBottom / VIEWPORT_SCROLL_EDGE;
    return AUTO_SCROLL_MAX_SPEED * intensity;
  }

  if (tabbedBoundary) {
    const nearScrollEdge =
      distFromTop < VIEWPORT_SCROLL_EDGE ||
      distFromBottom < VIEWPORT_SCROLL_EDGE ||
      nearContainerTop ||
      nearContainerBottom;

    if (
      dragDeltaY < -DRAG_DIRECTION_THRESHOLD &&
      scrollRoomDown > SCROLL_POSITION_EPSILON &&
      nearScrollEdge
    ) {
      return (
        AUTO_SCROLL_MAX_SPEED * Math.min(1, Math.abs(dragDeltaY) / 14)
      );
    }

    if (
      dragDeltaY > DRAG_DIRECTION_THRESHOLD &&
      scrollRoomUp > SCROLL_POSITION_EPSILON
    ) {
      return (
        -AUTO_SCROLL_MAX_SPEED * Math.min(1, dragDeltaY / 14)
      );
    }
  } else {
    if (dragDeltaY > DRAG_DIRECTION_THRESHOLD && scrollRoomUp > 0) {
      return (
        -AUTO_SCROLL_MAX_SPEED * Math.min(1, dragDeltaY / 14)
      );
    }

    if (dragDeltaY < -DRAG_DIRECTION_THRESHOLD && scrollRoomDown > 0) {
      return (
        AUTO_SCROLL_MAX_SPEED * Math.min(1, Math.abs(dragDeltaY) / 14)
      );
    }
  }

  return 0;
}

function getContainerAutoScrollDelta(
  clampedX: number,
  clampedY: number,
  rawX: number,
  rawY: number,
  dragDeltaY: number,
  container: HTMLElement,
  scrollRoot: HTMLElement,
  tabbedBoundary: HTMLElement | null,
): number {
  const containerRect = container.getBoundingClientRect();
  const scrollRect = scrollRoot.getBoundingClientRect();
  if (
    !shouldAutoScroll(rawX, rawY, containerRect, scrollRect, tabbedBoundary)
  ) {
    return 0;
  }

  const viewportDelta = getViewportEdgeScrollDelta(
    rawY,
    dragDeltaY,
    scrollRoot,
    tabbedBoundary,
    containerRect,
  );
  if (viewportDelta !== 0) {
    return viewportDelta;
  }

  const maxScrollTop = Math.max(
    0,
    scrollRoot.scrollHeight - scrollRoot.clientHeight,
  );
  const scrollRoomDown = maxScrollTop - scrollRoot.scrollTop;
  const scrollRoomUp = getTabbedScrollRoomUp(scrollRoot, tabbedBoundary);
  const edgeY = tabbedBoundary ? rawY : clampedY;
  const relY = edgeY - containerRect.top;
  const bottomEdgeStart = containerRect.height - CONTAINER_SCROLL_EDGE;

  if (
    relY < CONTAINER_SCROLL_EDGE &&
    scrollRoomUp > SCROLL_POSITION_EPSILON
  ) {
    if (
      tabbedBoundary &&
      dragDeltaY < -DRAG_DIRECTION_THRESHOLD &&
      scrollRoomDown > SCROLL_POSITION_EPSILON
    ) {
      const intensity = Math.min(1, Math.abs(dragDeltaY) / 14);
      return AUTO_SCROLL_MAX_SPEED * intensity;
    }

    const intensity = 1 - Math.max(0, relY) / CONTAINER_SCROLL_EDGE;
    return -AUTO_SCROLL_MAX_SPEED * intensity;
  }

  if (
    relY > bottomEdgeStart &&
    scrollRoomDown > SCROLL_POSITION_EPSILON
  ) {
    const intensity = Math.min(
      1,
      (relY - bottomEdgeStart) / CONTAINER_SCROLL_EDGE,
    );
    return AUTO_SCROLL_MAX_SPEED * intensity;
  }

  return 0;
}

function clampScrollDelta(
  deltaY: number,
  scrollRoot: HTMLElement,
  messageBoundary: HTMLElement | null,
): number {
  if (!deltaY) return 0;

  const maxScrollTop = Math.max(
    0,
    scrollRoot.scrollHeight - scrollRoot.clientHeight,
  );

  if (deltaY > 0) {
    const scrollRoom = maxScrollTop - scrollRoot.scrollTop;
    return scrollRoom <= 0 ? 0 : Math.min(deltaY, scrollRoom);
  }

  const scrollRoom = messageBoundary
    ? getTabbedScrollRoomUp(scrollRoot, messageBoundary)
    : scrollRoot.scrollTop;
  return scrollRoom <= 0 ? 0 : Math.max(deltaY, -scrollRoom);
}

function applyAutoScroll(
  scrollRoot: HTMLElement,
  deltaY: number,
  messageBoundary: HTMLElement | null,
) {
  const clampedDelta = clampScrollDelta(deltaY, scrollRoot, messageBoundary);
  if (!clampedDelta) return;
  scrollRoot.scrollTop += clampedDelta;
}

export function useOptionTransfer({
  disabled,
  isBrowserLocked,
  isOffline = false,
  containerRef,
}: UseOptionTransferArgs) {
  const prefersTouch = useMediaQuery("(pointer: coarse)");
  const [dragOptionId, setDragOptionId] = useState<string | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [touchGhost, setTouchGhost] = useState<TouchGhostState | null>(null);
  const [activeDropId, setActiveDropId] = useState<string | null>(null);
  const activeDragIdRef = useRef<string | null>(null);
  const activeDragLabelRef = useRef("");
  const pointerMovedRef = useRef(false);
  const scrollRootRef = useRef<HTMLElement | null>(null);
  const tabbedBoundaryRef = useRef<HTMLElement | null>(null);
  const lastPointerRef = useRef({
    x: 0,
    y: 0,
    rawX: 0,
    rawY: 0,
    dragDeltaY: 0,
  });
  const prevDragPointerRef = useRef<{ rawY: number } | null>(null);
  const autoScrollRafRef = useRef<number | null>(null);
  const dropHandlersRef = useRef<Map<string, (optionId: string) => void>>(
    new Map(),
  );

  const canInteract = !disabled && !isBrowserLocked && !isOffline;

  const rejectIfCannotInteract = useCallback(() => {
    if (disabled || isBrowserLocked) return true;
    if (isOffline) {
      notifyOfflineActionBlocked();
      return true;
    }
    return false;
  }, [disabled, isBrowserLocked, isOffline]);

  const stopAutoScrollLoop = useCallback(() => {
    if (autoScrollRafRef.current !== null) {
      cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = null;
    }
  }, []);

  const computeAutoScrollDelta = useCallback(
    (
      clampedX: number,
      clampedY: number,
      rawX: number,
      rawY: number,
      dragDeltaY: number,
    ) => {
      const container = containerRef?.current;
      const scrollRoot = scrollRootRef.current;
      if (!container || !scrollRoot) return 0;

      return clampScrollDelta(
        getContainerAutoScrollDelta(
          clampedX,
          clampedY,
          rawX,
          rawY,
          dragDeltaY,
          container,
          scrollRoot,
          tabbedBoundaryRef.current,
        ),
        scrollRoot,
        tabbedBoundaryRef.current,
      );
    },
    [containerRef],
  );

  const maybeAutoScroll = useCallback(
    (
      clampedX: number,
      clampedY: number,
      rawX: number,
      rawY: number,
      dragDeltaY: number,
    ) => {
      const container = containerRef?.current;
      const scrollRoot = scrollRootRef.current;
      if (!container || !scrollRoot) return;

      const deltaY = computeAutoScrollDelta(
        clampedX,
        clampedY,
        rawX,
        rawY,
        dragDeltaY,
      );
      if (deltaY !== 0) {
        applyAutoScroll(scrollRoot, deltaY, tabbedBoundaryRef.current);
      }
    },
    [computeAutoScrollDelta, containerRef],
  );

  const resolveDragPoint = useCallback(
    (clientX: number, clientY: number) => {
      const container = containerRef?.current;
      const rect = container?.getBoundingClientRect();
      if (!rect) {
        return { clientX, clientY, rect: null as DOMRect | null };
      }

      const clamped = clampToContainer(clientX, clientY, rect);
      return { clientX: clamped.x, clientY: clamped.y, rect };
    },
    [containerRef],
  );

  const tickAutoScroll = useCallback(() => {
    if (!activeDragIdRef.current) {
      stopAutoScrollLoop();
      return;
    }

    const { x, y, rawX, rawY, dragDeltaY } = lastPointerRef.current;
    maybeAutoScroll(x, y, rawX, rawY, dragDeltaY);

    const { clientX: dragX, clientY: dragY, rect } =
      resolveDragPoint(rawX, rawY);
    if (rect) {
      setTouchGhost({
        text: activeDragLabelRef.current,
        relativeX: dragX - rect.left,
        relativeY: dragY - rect.top,
      });
    }

    const stillInEdge =
      computeAutoScrollDelta(x, y, rawX, rawY, dragDeltaY) !== 0;

    if (stillInEdge) {
      autoScrollRafRef.current = requestAnimationFrame(tickAutoScroll);
    } else {
      stopAutoScrollLoop();
    }
  }, [
    computeAutoScrollDelta,
    maybeAutoScroll,
    resolveDragPoint,
    stopAutoScrollLoop,
  ]);

  const syncAutoScrollLoop = useCallback(
    (
      clampedX: number,
      clampedY: number,
      rawX: number,
      rawY: number,
      dragDeltaY: number,
    ) => {
      if (!activeDragIdRef.current) {
        stopAutoScrollLoop();
        return;
      }

      const deltaY = computeAutoScrollDelta(
        clampedX,
        clampedY,
        rawX,
        rawY,
        dragDeltaY,
      );

      if (deltaY !== 0 && autoScrollRafRef.current === null) {
        autoScrollRafRef.current = requestAnimationFrame(tickAutoScroll);
      } else if (deltaY === 0) {
        stopAutoScrollLoop();
      }
    },
    [computeAutoScrollDelta, stopAutoScrollLoop, tickAutoScroll],
  );

  const beginDragSession = useCallback(() => {
    const container = containerRef?.current ?? null;
    scrollRootRef.current = resolveScrollRoot(container);
    const inAssessment = container?.closest("[data-assessment-scroll]");
    tabbedBoundaryRef.current = !inAssessment
      ? ((container?.closest("[data-tabbed-message]") as HTMLElement | null) ??
        (container?.closest("[data-tabbed-scroll]") as HTMLElement | null))
      : null;
  }, [containerRef]);

  const clearDrag = useCallback(() => {
    stopAutoScrollLoop();
    scrollRootRef.current = null;
    tabbedBoundaryRef.current = null;
    activeDragIdRef.current = null;
    activeDragLabelRef.current = "";
    setDragOptionId(null);
    setTouchGhost(null);
    setActiveDropId(null);
    pointerMovedRef.current = false;
    prevDragPointerRef.current = null;
  }, [stopAutoScrollLoop]);

  const toggleSelect = useCallback(
    (optionId: string) => {
      if (rejectIfCannotInteract()) return;
      setSelectedOptionId((prev) => (prev === optionId ? null : optionId));
    },
    [rejectIfCannotInteract],
  );

  const placeSelected = useCallback(
    (dropId: string) => {
      if (rejectIfCannotInteract() || !selectedOptionId) return;
      dropHandlersRef.current.get(dropId)?.(selectedOptionId);
      setSelectedOptionId(null);
    },
    [rejectIfCannotInteract, selectedOptionId],
  );

  const updateDragPosition = useCallback(
    (clientX: number, clientY: number) => {
      const { clientX: dragX, clientY: dragY, rect } =
        resolveDragPoint(clientX, clientY);

      const dragDeltaY = prevDragPointerRef.current
        ? prevDragPointerRef.current.rawY - clientY
        : 0;
      prevDragPointerRef.current = { rawY: clientY };

      lastPointerRef.current = {
        x: dragX,
        y: dragY,
        rawX: clientX,
        rawY: clientY,
        dragDeltaY,
      };

      if (rect) {
        maybeAutoScroll(dragX, dragY, clientX, clientY, dragDeltaY);
        syncAutoScrollLoop(dragX, dragY, clientX, clientY, dragDeltaY);
      }

      if (rect) {
        setTouchGhost({
          text: activeDragLabelRef.current,
          relativeX: dragX - rect.left,
          relativeY: dragY - rect.top,
        });
      } else {
        setTouchGhost(null);
      }

      const target = document.elementFromPoint(clientX, clientY);
      const dropEl = target?.closest(
        `[${DROP_TARGET_ATTR}]`,
      ) as HTMLElement | null;
      setActiveDropId(dropEl?.getAttribute(DROP_TARGET_ATTR) ?? null);
    },
    [
      maybeAutoScroll,
      resolveDragPoint,
      syncAutoScrollLoop,
    ],
  );

  const finishDragAtPoint = useCallback(
    (clientX: number, clientY: number) => {
      if (!activeDragIdRef.current) {
        clearDrag();
        return;
      }

      const target = document.elementFromPoint(clientX, clientY);
      const dropEl = target?.closest(
        `[${DROP_TARGET_ATTR}]`,
      ) as HTMLElement | null;
      const dropId = dropEl?.getAttribute(DROP_TARGET_ATTR);

      if (dropId) {
        dropHandlersRef.current.get(dropId)?.(activeDragIdRef.current);
        setSelectedOptionId(null);
      }

      clearDrag();
    },
    [clearDrag],
  );

  const getPoolOptionHandlers = useCallback(
    (optionId: string, optionLabel: string) => {
      const handleClick = () => {
        if (pointerMovedRef.current) return;
        toggleSelect(optionId);
      };

      if (prefersTouch) {
        return {
          onTouchStart: (event: React.TouchEvent) => {
            if (rejectIfCannotInteract()) return;

            pointerMovedRef.current = false;
            activeDragIdRef.current = optionId;
            activeDragLabelRef.current = optionLabel;
            setDragOptionId(optionId);
            beginDragSession();

            const touch = event.touches[0];
            if (touch) {
              updateDragPosition(touch.clientX, touch.clientY);
            }

            const handleTouchMove = (moveEvent: TouchEvent) => {
              moveEvent.preventDefault();
              pointerMovedRef.current = true;
              const moveTouch = moveEvent.touches[0];
              if (!moveTouch) return;
              updateDragPosition(moveTouch.clientX, moveTouch.clientY);
            };

            const handleTouchEnd = (endEvent: TouchEvent) => {
              document.removeEventListener("touchmove", handleTouchMove);
              document.removeEventListener("touchend", handleTouchEnd);
              document.removeEventListener("touchcancel", handleTouchEnd);

              const endTouch = endEvent.changedTouches[0];
              if (!endTouch || !pointerMovedRef.current) {
                activeDragIdRef.current = null;
                clearDrag();
                return;
              }

              finishDragAtPoint(endTouch.clientX, endTouch.clientY);
            };

            document.addEventListener("touchmove", handleTouchMove, {
              passive: false,
            });
            document.addEventListener("touchend", handleTouchEnd);
            document.addEventListener("touchcancel", handleTouchEnd);

            event.stopPropagation();
          },
          onClick: handleClick,
        };
      }

      return {
        onPointerDown: (event: React.PointerEvent) => {
          if (event.button !== 0 || rejectIfCannotInteract()) return;

          const startX = event.clientX;
          const startY = event.clientY;
          pointerMovedRef.current = false;
          activeDragIdRef.current = null;
          activeDragLabelRef.current = optionLabel;

          const captureTarget = event.currentTarget as HTMLElement;
          captureTarget.setPointerCapture(event.pointerId);

          const handlePointerMove = (moveEvent: PointerEvent) => {
            const dx = moveEvent.clientX - startX;
            const dy = moveEvent.clientY - startY;
            if (
              !pointerMovedRef.current &&
              Math.hypot(dx, dy) < DRAG_THRESHOLD_PX
            ) {
              return;
            }

            if (!pointerMovedRef.current) {
              pointerMovedRef.current = true;
              activeDragIdRef.current = optionId;
              setDragOptionId(optionId);
              beginDragSession();
            }

            moveEvent.preventDefault();
            updateDragPosition(moveEvent.clientX, moveEvent.clientY);
          };

          const finishPointerDrag = (upEvent: PointerEvent) => {
            if (captureTarget.hasPointerCapture(upEvent.pointerId)) {
              captureTarget.releasePointerCapture(upEvent.pointerId);
            }

            document.removeEventListener("pointermove", handlePointerMove);
            document.removeEventListener("pointerup", finishPointerDrag);
            document.removeEventListener("pointercancel", finishPointerDrag);

            if (!pointerMovedRef.current || !activeDragIdRef.current) {
              activeDragIdRef.current = null;
              clearDrag();
              return;
            }

            finishDragAtPoint(upEvent.clientX, upEvent.clientY);
          };

          document.addEventListener("pointermove", handlePointerMove, {
            passive: false,
          });
          document.addEventListener("pointerup", finishPointerDrag);
          document.addEventListener("pointercancel", finishPointerDrag);

          event.preventDefault();
          event.stopPropagation();
        },
        onClick: handleClick,
      };
    },
    [
      prefersTouch,
      rejectIfCannotInteract,
      toggleSelect,
      updateDragPosition,
      clearDrag,
      finishDragAtPoint,
      beginDragSession,
    ],
  );

  const getDropTargetHandlers = useCallback(
    (dropId: string, onPlace: (optionId: string) => void) => {
      dropHandlersRef.current.set(dropId, onPlace);

      return {
        [DROP_TARGET_ATTR]: dropId,
        onClick: () => placeSelected(dropId),
      };
    },
    [placeSelected],
  );

  const getPoolHint = useCallback(
    (desktop: string, touch: string) => (prefersTouch ? touch : desktop),
    [prefersTouch],
  );

  const getOptionHighlight = useCallback(
    (optionId: string): "neutral" | undefined => {
      if (selectedOptionId === optionId) return "neutral";
      return undefined;
    },
    [selectedOptionId],
  );

  const isDragging = useCallback(
    (optionId: string) => dragOptionId === optionId,
    [dragOptionId],
  );

  const isDropTargetActive = useCallback(
    (dropId: string) => touchGhost !== null && activeDropId === dropId,
    [activeDropId, touchGhost],
  );

  return {
    prefersTouch,
    touchGhost,
    selectedOptionId,
    isDragActive: dragOptionId !== null,
    getPoolOptionHandlers,
    getDropTargetHandlers,
    getPoolHint,
    getOptionHighlight,
    isDragging,
    isDropTargetActive,
  };
}
